import { Localized, useLocalization } from '@fluent/react'
import Button from '@mui/material/Button'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useDataGridCustomLocaleText } from '../../../app/hooks'
import { useEffect, useState } from 'react'
import { unwrapResult } from '@reduxjs/toolkit'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { ErrorContainer } from '../../../app/ErrorContainer'
import { LoadingContainer } from '../../../app/LoadingContainer'
import { ButtonGroup, Paper, TableContainer } from '@mui/material'
import { QuickSearchToolbar } from '../../../app/QuickSearchToolbar'
import { FlexDiv } from '../../../app/Utils'
import { AddFabButton } from '../../../app/components/AddFabButton'
import { loadWorkflowDefinitions } from './api/workflowApi'
import { WorkflowDefinition } from './api/workflowTypes'
import { CreateWorkflowDefinitionDialog } from './dialogs/CreateWorkflowDefinitionDialog'

const EDIT_PATH = (id: string) => `/workflow-definitions/edit/${id}`;


export const WorkflowDefinitionList = () => {
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [workflowDefinition, setWorkflowDefinition] = useState<WorkflowDefinition[]>([]);
    const localeText = useDataGridCustomLocaleText();
    const [createDialogVisible, setCreateDialogVisible] = useState(false);
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);


    useEffect(() => {
        setIsLoading(true)
        dispatch(loadWorkflowDefinitions())
            .then(unwrapResult)
            .then(setWorkflowDefinition)
            .catch((error) => { console.log(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false));
    }, [])

    const handleGoToClick = (id) => {
        navigate(EDIT_PATH(id));
    };

    const onSucces = (template) => {
        setCreateDialogVisible(false);
        handleGoToClick(template.id)
    }

    const handleHideDialog = () => {
        setCreateDialogVisible(false);
    }

    const openCreateDialog = () => {
        setCreateDialogVisible(true);
    };

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: l10n.getString('wfl-name', null, 'WFL name'),
            width: 200,
        },
        {
            field: 'objectType',
            headerName: l10n.getString('wfl-object-type', null, 'WFL Type'),
            width: 300,
        },
        {
            field: 'objectSubType',
            headerName: l10n.getString('wfl-object-type', null, 'Object sub type'),
            width: 300,
        },
        {
            field: 'version',
            headerName: l10n.getString('wfl-object-version', null, 'Version'),
            width: 300,
        },
        {
            field: 'actions', type: 'actions', width: 300,
            renderCell: (params) => {
                return (
                    <ButtonGroup>
                        <Button onClick={() => handleGoToClick(params.row.id)} color="secondary" variant="outlined">
                            <Localized id="go-to">
                                <span>_go</span>
                            </Localized>
                        </Button>

                    </ButtonGroup>
                )
            }
        }
    ];


    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    return <>
        <TableContainer component={Paper}>
            <div style={{ display: 'flex', height: '100%' }}>
                <div style={{ flexGrow: 1 }}>
                    <DataGrid
                        style={{ minHeight: 100, height: 'calc(100vh - 250px)' }}
                        localeText={localeText}
                        rows={workflowDefinition}
                        columns={columns}
                        slots={{ toolbar: QuickSearchToolbar }}
                        onRowDoubleClick={({ row }) => handleGoToClick(row.id)} />
                </div>
            </div>
            <FlexDiv>
                <AddFabButton onClick={openCreateDialog} />
            </FlexDiv>
        </TableContainer>
        {
            createDialogVisible && <CreateWorkflowDefinitionDialog onSucces={onSucces} onCancel={handleHideDialog} />
        }
    </>
}
